import { useCallback, useEffect, useMemo, useState } from 'react';

import { Box, styled, Typography } from '@mui/material';
import { useEvent } from 'react-use';

import ChartShell from '@/components/Chart/ChartShell';
import { VisibilityContext } from '@/components/Chart/FullScreenChart/FullScreenLegend/LegendItem/VisibilityContext';
import type { ExtendedChart, ExtendedSeriesOptionsType } from '@/components/Chart/types/highcharts';
import { EmptyState } from '@/components/EmptyState';
import Loader from '@/components/Loader';
import { useForceUpdate } from '@/hooks/useForceUpdate';

import FullScreenLegend from './FullScreenLegend';
import useFullScreenChart from './useFullScreenChart';

import type { BoxProps } from '@mui/material';

interface ChartContainerProps extends BoxProps {
  isLightMode: boolean;
}

// Create a styled Box with typed props
export const ChartContainer = styled(Box, {
  shouldForwardProp: prop => prop !== 'isLightMode'
})<ChartContainerProps>(({ theme, isLightMode }) => ({
  '.highcharts-annotation .highcharts-crooked-lines,.highcharts-annotation .highcharts-vertical-line':
    {
      stroke: isLightMode ? theme.palette.background.paper : theme.palette.text.primary
    },
  'svg marker, svg marker path': {
    color: isLightMode ? theme.palette.background.paper : theme.palette.text.primary,
    fill: isLightMode ? theme.palette.background.paper : theme.palette.text.primary
  }
}));

const FullScreenChart = ({
  chartSeries,
  error,
  loading,
  isDataHub,
  isLightMode,
  tabName
}: {
  chartSeries: ExtendedSeriesOptionsType[] | undefined;
  error: boolean;
  loading: boolean;
  isDataHub?: boolean;
  isLightMode: boolean;
  tabName: string;
}) => {
  const { options, chartComponentRef, toggleSeriesVisibility } = useFullScreenChart({
    chartSeries,
    isDataHub,
    isLightMode
  });
  const [chartOptions, setChartOptions] = useState<any>(undefined);

  const forceUpdate = useForceUpdate();

  const handleResize = useCallback(() => {
    const newHeight = window.innerHeight / 2;

    if (chartComponentRef.current) {
      chartComponentRef.current.chart.update(
        {
          chart: {
            height: newHeight
          }
        },
        false
      );
      forceUpdate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceUpdate]);

  useEvent('resize', handleResize, window);

  useEffect(() => {
    if (!options) return;

    // Check if chart is already mounted
    if (chartComponentRef.current) {
      const chart = chartComponentRef.current.chart as ExtendedChart;
      const selected = chart.rangeSelector?.selected ?? 0;
      const extendedOptions = {
        ...options,
        rangeSelector: {
          ...options.rangeSelector,
          selected: selected < 3 ? selected : undefined
        }
      };

      chart.update(extendedOptions);
    } else {
      // Chart not mounted yet, set initial options
      setChartOptions(options);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  const providerValue = useMemo(() => ({ toggleSeriesVisibility }), [toggleSeriesVisibility]);

  if (error)
    return (
      <Box
        sx={{
          width: '100%',

          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          flexGrow: 1
        }}
      >
        <Typography color='info'>
          We couldn't retrieve your data. Please try again later.
        </Typography>
      </Box>
    );
  if (!loading && !chartSeries?.length) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          flexGrow: 1
        }}
      >
        <EmptyState />
      </Box>
    );
  }

  if (loading || !chartSeries?.length)
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          flexGrow: 1
        }}
      >
        <Loader />
      </Box>
    );

  if (chartOptions) {
    return (
      <Box
        className='chart-container'
        sx={{ backgroundColor: 'background.paper', position: 'relative ', width: '100%' }}
      >
        <Box sx={{ position: 'absolute', top: 10, right: 15, zIndex: 1 }} />
        <ChartContainer
          className={`fullscreen-chart ${isLightMode ? 'light' : 'dark'}`}
          isLightMode={isLightMode}
        >
          <ChartShell
            isStockChart
            chartComponentRef={chartComponentRef}
            chartId={tabName}
            options={chartOptions}
          />
        </ChartContainer>
        {options && (
          <VisibilityContext.Provider value={providerValue}>
            <FullScreenLegend
              isFullTitle={isDataHub}
              options={options}
            />
          </VisibilityContext.Provider>
        )}
      </Box>
    );
  }
};
export default FullScreenChart;
